import apiClient, { generateSessionId } from './apiClient';
import { AUTH_CONFIG } from './config';

// Use apiClient instead of direct axios instance
const api = apiClient;


// Auth service
const authService = {
  // Register a new tenant with admin user
  registerTenant: async (tenantData) => {
    try {
      const response = await api.post('/auth/register-tenant', tenantData);
      if (response.data.token) {
        // Generate a new session ID for this login
        const sessionId = generateSessionId();
        localStorage.setItem(AUTH_CONFIG.SESSION_KEY, sessionId);
        
        localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, response.data.token);
        localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(response.data.user));
        localStorage.setItem(AUTH_CONFIG.TENANT_KEY, JSON.stringify(response.data.tenant));
      }
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error.response?.data || { message: 'Registration failed' };
    }
  },

  // Register a new user within a tenant (admin only)
  registerUser: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('User registration error:', error);
      throw error.response?.data || { message: 'User registration failed' };
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      // Generate a new session ID for this login attempt
      const sessionId = generateSessionId();
      localStorage.setItem(AUTH_CONFIG.SESSION_KEY, sessionId);
      
      // Include session ID in login request
      const response = await api.post('/auth/login', {
        ...credentials,
        sessionId
      });
      
      if (response.data.token) {
        localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, response.data.token);
        localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(response.data.user));
        localStorage.setItem(AUTH_CONFIG.TENANT_KEY, JSON.stringify(response.data.tenant));
        
        // Console log the token for debugging
        console.log('Login successful. Token:', response.data.token);
        console.log('User data:', response.data.user);
        console.log('Tenant data:', response.data.tenant);
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      // Clear any previous session data on failed login
      localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
      
      // Properly format the error to be handled by the UI
      if (error.response) {
        throw {
          status: error.response.status,
          message: error.response.data.message || 'Invalid credentials',
          response: error.response
        };
      } else {
        throw { message: error.message || 'Login failed. Please try again.' };
      }
    }
  },

  // Firebase login with Google
  firebaseLogin: async (userData) => {
    try {
      const sessionId = generateSessionId();
      localStorage.setItem(AUTH_CONFIG.SESSION_KEY, sessionId);
      
      console.log('Firebase auth data:', userData);
      
      const response = await api.post('/auth/firebase-login', {
        ...userData,
        sessionId
      });
      
      const { token, user, tenant } = response.data;
      
      // Store auth data in localStorage
      localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
      localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user));
      localStorage.setItem(AUTH_CONFIG.TENANT_KEY, JSON.stringify(tenant));
      
      // Console log the token for debugging
      console.log('Firebase login successful. Token:', token);
      console.log('User data:', user);
      console.log('Tenant data:', tenant);
      
      return response.data;
    } catch (error) {
      console.error('Firebase login error:', error);
      throw error.response?.data || { message: 'Firebase login failed' };
    }
  },

  // Get current user profile
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error.response?.data || { message: 'Error fetching user profile' };
    }
  },

  // Logout user
  logout: async () => {
    try {
      // Notify the server about logout
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of server response
      localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
      localStorage.removeItem(AUTH_CONFIG.USER_KEY);
      localStorage.removeItem(AUTH_CONFIG.TENANT_KEY);
      localStorage.removeItem(AUTH_CONFIG.SESSION_KEY);
    }
  },

  // Check if user is logged in
  isLoggedIn: () => {
    return !!localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  },

  // Get current user from local storage
  getUser: () => {
    const user = localStorage.getItem(AUTH_CONFIG.USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  // Get current tenant from local storage
  getTenant: () => {
    const tenant = localStorage.getItem(AUTH_CONFIG.TENANT_KEY);
    return tenant ? JSON.parse(tenant) : null;
  }
};

const productService = {
  getProducts: async (page = 1, limit = 10) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.get(`/products?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  createProduct: async (formData) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.post(`/products`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  updateProduct: async (id, formData) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.put(`/products/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  getProduct: async (id) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  deleteProduct: async (id) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }
};

// Customer Service
const customerService = {
  getCustomers: async (page = 1, limit = 10) => {
    try {
      const response = await api.get(`/customers?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error.response?.data || { message: 'Error fetching customers' };
    }
  },

  getCustomer: async (id) => {
    try {
      const response = await api.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer:', error);
      throw error.response?.data || { message: 'Error fetching customer' };
    }
  },

  createCustomer: async (customerData) => {
    try {
      const response = await api.post('/customers', customerData);
      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error.response?.data || { message: 'Error creating customer' };
    }
  },

  updateCustomer: async (id, customerData) => {
    try {
      const response = await api.put(`/customers/${id}`, customerData);
      return response.data;
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error.response?.data || { message: 'Error updating customer' };
    }
  },

  deleteCustomer: async (id) => {
    try {
      const response = await api.delete(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error.response?.data || { message: 'Error deleting customer' };
    }
  }
};

// Supplier service
const supplierService = {
  // Get all suppliers with pagination
  getSuppliers: async (page = 1, limit = 10) => {
    try {
      const response = await api.get(`/suppliers?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error.response?.data || { message: 'Error fetching suppliers' };
    }
  },

  // Get single supplier by ID
  getSupplier: async (id) => {
    try {
      const response = await api.get(`/suppliers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier:', error);
      throw error.response?.data || { message: 'Error fetching supplier' };
    }
  },

  // Create new supplier
  createSupplier: async (supplierData) => {
    try {
      const response = await api.post('/suppliers', supplierData);
      return response.data;
    } catch (error) {
      console.error('Error creating supplier:', error);
      
      // Check if we have detailed validation errors
      if (error.response?.data?.errors) {
        const validationError = new Error(error.response.data.message || 'Validation failed');
        validationError.validationErrors = error.response.data.errors;
        throw validationError;
      }
      
      throw error.response?.data || { message: 'Error creating supplier' };
    }
  },

  // Update supplier
  updateSupplier: async (id, supplierData) => {
    try {
      const response = await api.put(`/suppliers/${id}`, supplierData);
      return response.data;
    } catch (error) {
      console.error('Error updating supplier:', error);
      
      // Check if we have detailed validation errors
      if (error.response?.data?.errors) {
        const validationError = new Error(error.response.data.message || 'Validation failed');
        validationError.validationErrors = error.response.data.errors;
        throw validationError;
      }
      
      throw error.response?.data || { message: 'Error updating supplier' };
    }
  },

  // Delete supplier
  deleteSupplier: async (id) => {
    try {
      const response = await api.delete(`/suppliers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error.response?.data || { message: 'Error deleting supplier' };
    }
  }
};

// Invitation service
const invitationService = {
  // Get all invitations for current tenant
  getInvitations: async () => {
    try {
      const response = await api.get('/invitations');
      return response.data;
    } catch (error) {
      console.error('Error fetching invitations:', error);
      throw error.response?.data || { message: 'Error fetching invitations' };
    }
  },

  // Create a new invitation
  createInvitation: async (data) => {
    try {
      const response = await api.post('/invitations', {
        email: data.email,
        role: data.role,
        permissions: data.permissions
      });
      return response.data;
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw error.response?.data || { message: 'Error creating invitation' };
    }
  },

  // Resend an invitation
  resendInvitation: async (invitationId) => {
    try {
      const response = await api.post(`/invitations/${invitationId}/resend`);
      return response.data;
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw error.response?.data || { message: 'Error resending invitation' };
    }
  },

  // Cancel an invitation
  cancelInvitation: async (invitationId) => {
    try {
      const response = await api.post(`/invitations/${invitationId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      throw error.response?.data || { message: 'Error cancelling invitation' };
    }
  },

  // Verify invitation token
  verifyInvitation: async (token) => {
    try {
      const response = await api.get(`/invitations/verify/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error verifying invitation:', error);
      throw error.response?.data || { message: 'Error verifying invitation' };
    }
  },

  // Accept invitation
  acceptInvitation: async (token, userData) => {
    try {
      const response = await api.post(`/invitations/accept/${token}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error.response?.data || { message: 'Error accepting invitation' };
    }
  }
};

// Draft Invoice service
const draftInvoiceService = {
  // Create a new draft invoice
  createDraftInvoice: async (draftData) => {
    try {
      const response = await api.post('/draft-invoices', draftData);
      return response.data;
    } catch (error) {
      console.error('Error creating draft invoice:', error);
      throw error.response?.data || { message: 'Error creating draft invoice' };
    }
  },
  
  // Get all draft invoices with pagination
  getDraftInvoices: async (page = 1, limit = 10) => {
    try {
      const response = await api.get(`/draft-invoices?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching draft invoices:', error);
      throw error.response?.data || { message: 'Error fetching draft invoices' };
    }
  },
  
  // Get a single draft invoice by invoice number
  getDraftInvoice: async (invoiceNo) => {
    try {
      const response = await api.get(`/draft-invoices/${invoiceNo}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching draft invoice:', error);
      throw error.response?.data || { message: 'Error fetching draft invoice' };
    }
  },
  
  // Convert a draft invoice to a regular invoice
  convertDraftToInvoice: async (invoiceNo) => {
    try {
      const response = await api.post(`/draft-invoices/${invoiceNo}/convert`);
      return response.data;
    } catch (error) {
      console.error('Error converting draft invoice:', error);
      throw error.response?.data || { message: 'Error converting draft invoice' };
    }
  }
};

// Purchase service
const purchaseService = {
  // Get all purchases with pagination
  getPurchases: async () => {
    try {
      const response = await api.get('/purchases/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching purchases:', error);
      throw error;
    }
  },
  
  getPurchaseById: async (id) => {
    try {
      const response = await api.get(`/purchases/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching purchase details:', error);
      throw error;
    }
  },
  
  createPurchase: async (purchaseData) => {
    try {
      const response = await api.post('/purchases', purchaseData);
      return response.data;
    } catch (error) {
      console.error('Error creating purchase:', error);
      throw error;
    }
  },
  
  updatePurchase: async (id, purchaseData) => {
    try {
      const response = await api.put(`/purchases/${id}`, purchaseData);
      return response.data;
    } catch (error) {
      console.error('Error updating purchase:', error);
      throw error;
    }
  },
  
  deletePurchase: async (id) => {
    try {
      const response = await api.delete(`/purchases/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting purchase:', error);
      throw error;
    }
  },
  
  changePurchaseStatus: async (id, status) => {
    try {
      const response = await api.patch(`/purchases/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error changing purchase status:', error);
      throw error;
    }
  },
  
  getNextPurchaseNumber: async () => {
    try {
      const response = await api.get('/sequence/purchase-number');
      return response.data;
    } catch (error) {
      console.error('Error fetching next purchase number:', error);
      throw error;
    }
  }
};

// Masters service
const mastersService = {
  // Get all master data for current tenant
  getAllMasters: async (tenantId) => {
    try {
      const response = await api.get(`/masters/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching masters:', error);
      throw error.response?.data || { message: 'Error fetching masters' };
    }
  },

  // Create a new master item
  createMaster: async (masterType, tenantId, data) => {
    try {
      const response = await api.post(`/masters/${masterType}/${tenantId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating master item:', error);
      throw error.response?.data || { message: 'Error creating master item' };
    }
  },

  // Update a master item
  updateMaster: async (masterType, tenantId, id, data) => {
    try {
      const response = await api.put(`/masters/${masterType}/${tenantId}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating master item:', error);
      throw error.response?.data || { message: 'Error updating master item' };
    }
  },

  // Delete a master item
  deleteMaster: async (masterType, tenantId, id) => {
    try {
      const response = await api.delete(`/masters/${masterType}/${tenantId}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting master item:', error);
      throw error.response?.data || { message: 'Error deleting master item' };
    }
  }
};

export {
  api,
  authService,
  customerService,
  productService,
  supplierService,
  invitationService,
  purchaseService,
  mastersService,
  draftInvoiceService
};